import { Box } from '@mui/joy';
import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';
import type { Point } from 'geojson';
import { useEffect } from 'react';
import z from 'zod';

import { CreateReport } from '@/components/create-report/CreateReport';
import { PublicMap } from '@/components/map/PublicMap';
import { WelcomeModal } from '@/components/modals/WelcomeModal';
import { Footer } from '@/components/navigations/Footer';
import { useScreenSize } from '@/hooks/useScreenSize';
import { useServiceStationQuery } from '@/queries/useServiceStationQuery';
import { useCreateReportStore } from '@/stores/useCreateReportStore';
import { CreateReportStep } from '@/types/CreateReportStep';
import { LocationInputMode } from '@/types/LocationInputMode';

const zValidateSearch = z.object({
  laenge: z.number().optional().catch(undefined),
  breite: z.number().optional().catch(undefined),
  grenze: z.array(z.number()).length(4).optional().catch(undefined),
  zoom: z.number().optional().catch(undefined),

  standortnummer: z.string().optional().catch(undefined),
  'neue-meldung': z.boolean().optional().catch(undefined),
  servicestation: z.number().optional().catch(undefined),
});

export const Route = createFileRoute('/_public')({
  validateSearch: zValidateSearch,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
});

function RouteComponent() {
  const { updateData, setLocationInputMode, setStep, setExpanded, updateGeometryWithReverseGeocoding } =
    useCreateReportStore((state) => state.actions);

  const navigate = useNavigate();
  const search = Route.useSearch();
  const routeSignLocationId = search.standortnummer;
  const newReport = !!search['neue-meldung'];
  const serviceStationId = search.servicestation;
  const { data: serviceStation } = useServiceStationQuery(serviceStationId);

  const { isDesktop } = useScreenSize();

  useEffect(() => {
    (async () => {
      if (routeSignLocationId) {
        setLocationInputMode(LocationInputMode.ROUTE_SIGN);
        updateData({
          routeSignLocationId,
          category: undefined,
          serviceStationId: undefined,
        });
        setStep(CreateReportStep.LOCATION);
        setExpanded(true);
        await navigate({
          to: '.',
          search: (prev) => ({
            ...prev,
            standortnummer: undefined,
          }),
        });
      } else if (serviceStationId && serviceStation) {
        setLocationInputMode(LocationInputMode.DEFAULT);
        updateData({
          serviceStationId: serviceStation.properties.id,
          category: undefined,
          geometry: serviceStation.geometry,
          routeSignLocationId: undefined,
        });
        await updateGeometryWithReverseGeocoding(serviceStation.geometry);
        setStep(CreateReportStep.LOCATION);
        setExpanded(true);
        await navigate({
          to: '.',
          search: (prev) => ({
            ...prev,
            servicestation: undefined,
          }),
        });
      } else if (newReport) {
        const longitudeString = search.laenge;
        const latitudeString = search.breite;

        if (longitudeString && latitudeString) {
          const longitude = Number(longitudeString);
          const latitude = Number(latitudeString);
          const geometry: Point = { type: 'Point', coordinates: [longitude, latitude] };

          setLocationInputMode(LocationInputMode.DEFAULT);
          await updateGeometryWithReverseGeocoding(geometry);
          updateData({
            category: undefined,
            serviceStationId: undefined,
          });
          setStep(CreateReportStep.LOCATION);
          setExpanded(true);
          await navigate({
            to: '.',
            search: (prev) => ({
              ...prev,
              'neue-meldung': undefined,
            }),
          });
        }
      }
    })();
  }, [
    navigate,
    newReport,
    routeSignLocationId,
    serviceStationId,
    search.breite,
    search.laenge,
    setExpanded,
    setLocationInputMode,
    setStep,
    updateData,
    updateGeometryWithReverseGeocoding,
    serviceStation,
  ]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <PublicMap>
        <CreateReport />

        <Outlet />

        <WelcomeModal />
      </PublicMap>
      {isDesktop ? <Footer /> : null}
    </Box>
  );
}
